// Which lead drafts the rep has already copied to the clipboard.
//
// A receipt can list fifty leads and the rep works down it one at a time,
// pasting each draft into Gmail. Without a mark there is nothing on screen
// separating "already sent this one" from "haven't touched it" — so the list
// gets re-copied, or worse, skipped. This is that mark.
//
// Deliberately local and per-user: it records what THIS person has done from
// THIS browser. It is a work-tracking convenience, not a claim about what was
// actually sent — the app never sees the paste, so `copied` means "copied",
// never "delivered".
import { currentScope } from './userScope.js'

const BASE = 'revailosales.copiedDrafts'
const listeners = new Set()

const key = (scope) => `${BASE}.${scope || 'anon'}`

function read() {
  try {
    const raw = JSON.parse(localStorage.getItem(key(currentScope())) || '{}')
    return raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {}
  } catch {
    // Corrupt or unavailable storage means "nothing copied yet" — never a throw
    // into a render.
    return {}
  }
}

function write(map) {
  try {
    localStorage.setItem(key(currentScope()), JSON.stringify(map))
  } catch {
    /* quota or private mode — the mark just won't persist */
  }
  listeners.forEach((fn) => {
    try {
      fn()
    } catch {
      /* a bad subscriber must not break the others */
    }
  })
}

/** Every copied lead key, as a Set — cheap for a list to test against. */
export function copiedSet() {
  return new Set(Object.keys(read()))
}

export function isDraftCopied(leadKey) {
  if (!leadKey) return false
  return Boolean(read()[String(leadKey)])
}

/** Record a copy. Returns the ISO stamp stored, so a caller can show "2m ago". */
export function markDraftCopied(leadKey) {
  if (!leadKey) return ''
  const at = new Date().toISOString()
  const map = read()
  map[String(leadKey)] = at
  // Bounded: the newest 500 marks. A rep working a list for months should not
  // accumulate an unbounded blob in a store shared with their threads.
  const entries = Object.entries(map).sort((a, b) => String(b[1]).localeCompare(String(a[1])))
  write(Object.fromEntries(entries.slice(0, 500)))
  return at
}

export function unmarkDraftCopied(leadKey) {
  const map = read()
  if (!map[String(leadKey)]) return false
  delete map[String(leadKey)]
  write(map)
  return true
}

export function subscribeCopied(fn) {
  listeners.add(fn)
  return () => listeners.delete(fn)
}
